import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkle,
  Calculator,
  Database,
  Calendar,
  CurrencyDollar,
  TrendUp,
  ArrowsLeftRight,
  ShieldCheck,
  ChatCircleDots,
  GlobeHemisphereWest,
  CheckCircle,
} from "@phosphor-icons/react";

const TICKER = [
  "15,053 tCO₂e measured today",
  "$788k carbon savings unlocked this week",
  "EN15978 · PAS 2080 · CBAM live",
  "Claude Sonnet 4.5 Copilot online",
  "52 EPDs · 20 countries · 13 frameworks",
];

const FEATURES = [
  { icon: Calculator, title: "Embodied A1–A5 Calculator", body: "BOQ, BIM, IFC inputs map to country-specific transport and EN15978 stage factors in seconds." },
  { icon: Database, title: "Global EPD Library", body: "Curated, sortable EPDs across concrete, steel, timber, glass, MEP, infrastructure & renewables." },
  { icon: Calendar, title: "Monthly Carbon Tracking", body: "Log emissions across materials, fuel, power, water, waste, logistics and travel." },
  { icon: CurrencyDollar, title: "Carbon Cost Intelligence", body: "EU ETS, UK ETS, CBAM and internal carbon pricing applied to every line." },
  { icon: TrendUp, title: "Forecast + Net-Zero Path", body: "12-month projection plus an annualised path to 2050 — see commitments crystallise." },
  { icon: ArrowsLeftRight, title: "Procurement Lock-in", body: "Gantt-style timeline that flags packages still switchable before procurement cut-off — with $ savings ticker." },
  { icon: ShieldCheck, title: "Compliance Reports", body: "EN15978, PAS 2080, ISO 14040/44/64, LEED, BREEAM, TCFD, CSRD — export PDF and DOCX." },
  { icon: ChatCircleDots, title: "AI Carbon Copilot", body: "Claude Sonnet 4.5 grounded in your project — ask anything from CLT swaps to 12-month exposure." },
];

const PRICING = [
  {
    name: "Individual",
    seats: "1 seat",
    items: [
      { id: "ind-monthly", price: 20, cycle: "/ month" },
      { id: "ind-quarterly", price: 54, cycle: "/ 3 months", note: "Save 10%" },
      { id: "ind-halfyearly", price: 102, cycle: "/ 6 months", note: "Save 15%" },
      { id: "ind-yearly", price: 192, cycle: "/ year", note: "Save 20%", popular: true },
    ],
    perks: ["Unlimited projects", "EPD library", "AI Copilot", "Compliance reports"],
  },
  {
    name: "Organisation",
    seats: "4 seats",
    items: [
      { id: "org-monthly", price: 60, cycle: "/ month" },
      { id: "org-quarterly", price: 162, cycle: "/ 3 months", note: "Save 10%" },
      { id: "org-halfyearly", price: 306, cycle: "/ 6 months", note: "Save 15%" },
      { id: "org-yearly", price: 576, cycle: "/ year", note: "Save 20%", popular: true },
    ],
    perks: ["4 seats · shared workspace", "Role-based access", "Procurement Lock-in", "Priority support"],
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0F1115] text-white overflow-hidden">
      {/* Top bar */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0F1115]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline">GPI</div>
            <div className="font-serif-display text-xl leading-none">CarbonIQ</div>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-ink-muted">
            <a href="#features" className="hover:text-white transition-colors">Modules</a>
            <a href="#procurement" className="hover:text-white transition-colors">Procurement Lock-in</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Link
              data-testid="landing-signin-btn"
              to="/login"
              className="text-sm text-ink-muted hover:text-white transition-colors"
            >
              Sign in
            </Link>
            <Link
              data-testid="landing-getstarted-btn"
              to="/register"
              className="bg-gold text-black px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#e0bc40] flex items-center gap-1"
            >
              Get started <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-40 pb-32">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/81e028a2-79ed-4385-90c0-b63c7473c54d/images/515a027af52868bf4f9426a2d226ae3c6110fc857fc174de4e2e0d3d534644e8.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 70%)",
          }}
        />
        <div className="absolute inset-0 bg-grid-fine opacity-50 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 px-4 py-1.5 rounded-full text-xs">
            <Sparkle size={12} weight="fill" className="text-gold animate-pulse-gold" />
            <span className="text-gold tracking-wider">CARBON × COST × SCHEDULE · 5D DIGITAL TWIN</span>
          </div>

          <h1 className="font-serif-display text-6xl md:text-8xl mt-8 leading-[0.95] tracking-tight">
            Make carbon<br />
            <span className="gradient-text-gold">a deal-making lever.</span>
          </h1>

          <p className="text-ink-muted text-lg md:text-xl mt-8 max-w-2xl mx-auto leading-relaxed">
            CarbonIQ measures embodied & operational carbon, prices it against EU ETS / CBAM / internal carbon, and
            tells your procurement team which packages are still switchable before the cut-off date — with the dollars
            it saves you on screen.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              data-testid="hero-cta-trial"
              to="/register"
              className="bg-gold text-black px-7 py-3.5 font-medium rounded-sm hover:bg-[#e0bc40] flex items-center gap-2 glow-gold"
            >
              Start 7-day trial <ArrowRight size={16} weight="bold" />
            </Link>
            <a
              href="#pricing"
              className="border border-white/15 px-7 py-3.5 text-sm hover:bg-white/[0.04] transition-colors rounded-sm"
            >
              See pricing
            </a>
          </div>

          <div className="mt-16 flex justify-center gap-8 text-xs font-mono-data text-ink-dim tracking-widest">
            <span>EN15978</span>
            <span>PAS 2080</span>
            <span>GHG PROTOCOL</span>
            <span>CBAM</span>
            <span>TCFD · CSRD</span>
          </div>
        </div>
      </section>

      {/* Live ticker */}
      <div className="border-y border-white/10 py-4 overflow-hidden bg-black">
        <div className="flex gap-12 whitespace-nowrap animate-ticker">
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <Sparkle size={10} weight="fill" className="text-gold" />
              <span className="text-ink-muted">{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Procurement Lock-in feature */}
      <section id="procurement" className="py-32 relative">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline">Flagship · Sales tool</div>
            <h2 className="font-serif-display text-5xl mt-3 leading-tight">
              The Procurement<br />
              <span className="text-gold">Carbon Lock-in</span> view.
            </h2>
            <p className="text-ink-muted text-lg mt-6 leading-relaxed">
              Every BOQ line carries a procurement cut-off date. Before that date you can switch to a lower-carbon
              alternative — after, the carbon is locked in. CarbonIQ shows the full Gantt, the days left on each
              package, and a live $ savings ticker driven by your project&apos;s carbon price.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Gantt timeline of procurement deadlines per package",
                "Best-in-class EPD alternative auto-matched per line",
                "kg-savings AND $-savings (your scheme: ETS, CBAM, internal)",
                "Lock items once committed — savings ticker updates live",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <CheckCircle size={16} weight="fill" className="text-eco shrink-0 mt-0.5" />
                  <span className="text-white">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-white/10 bg-[#1A1D24] p-6 glow-gold">
            <div className="overline">Live · Demo project</div>
            <div className="font-serif-display text-3xl mt-1">$788,190</div>
            <div className="text-xs text-ink-muted">Carbon savings still on the table — 10 packages, 14–106 days remaining.</div>
            <div className="mt-6 space-y-2">
              {[
                { name: "Concrete C30/37 — Substructure", days: 14, save: "$182k" },
                { name: "Steel Frame", days: 28, save: "$211k" },
                { name: "Aluminium Curtain Wall", days: 42, save: "$133k" },
                { name: "Low-E Triple Glazing", days: 56, save: "$98k" },
              ].map((row) => (
                <div key={row.name} className="flex items-center gap-3 text-xs">
                  <div className="flex-1 text-white truncate">{row.name}</div>
                  <div className="font-mono-data text-ink-muted">{row.days}d</div>
                  <div className="font-mono-data text-gold w-16 text-right">{row.save}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 h-1 bg-white/5 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 w-1/3 bg-gold animate-pulse-gold" />
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="py-32 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="overline">Eight modules · One platform</div>
            <h2 className="font-serif-display text-5xl mt-3">Everything carbon, in one place.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="border border-white/10 bg-[#1A1D24] p-6 hover:border-gold/30 transition-colors animate-slide-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <Icon size={22} weight="duotone" className="text-gold mb-4" />
                  <div className="font-serif-display text-xl">{f.title}</div>
                  <p className="text-sm text-ink-muted mt-2 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-32 border-t border-white/10 relative">
        <div className="absolute inset-0 bg-grid-fine opacity-30 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="overline">Plans</div>
            <h2 className="font-serif-display text-5xl mt-3">Carbon at the price of a good coffee.</h2>
            <p className="text-ink-muted mt-4 max-w-xl mx-auto">
              Start individual. Scale to a team. Cancel anytime. All prices in USD.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {PRICING.map((tier) => (
              <div key={tier.name} className="border border-white/10 bg-[#1A1D24] p-8">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="overline">{tier.seats}</div>
                    <div className="font-serif-display text-3xl mt-1">{tier.name}</div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-2">
                  {tier.items.map((p) => (
                    <Link
                      key={p.id}
                      to={`/register?plan=${p.id}`}
                      data-testid={`pricing-${p.id}`}
                      className={`relative border p-4 rounded-sm transition-colors ${
                        p.popular ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      {p.popular && (
                        <span className="absolute -top-2 right-2 text-[10px] tracking-widest uppercase text-gold bg-[#0F1115] px-2 py-0.5">
                          Best value
                        </span>
                      )}
                      <div className="font-mono-data text-3xl text-white">${p.price}</div>
                      <div className="text-xs text-ink-muted mt-1">{p.cycle}</div>
                      {p.note && <div className="text-[11px] text-eco mt-2">{p.note}</div>}
                    </Link>
                  ))}
                </div>

                <ul className="mt-6 space-y-2 text-sm">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="flex items-center gap-2 text-ink-muted">
                      <CheckCircle size={14} weight="fill" className="text-eco" /> {perk}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 text-sm text-ink-muted">
            Payments via Razorpay · USD billing · Cancel any time
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 border-t border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-fine opacity-40" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <GlobeHemisphereWest size={36} weight="duotone" className="text-gold mx-auto mb-6" />
          <h2 className="font-serif-display text-5xl md:text-6xl leading-tight">
            Carbon. Cost. Schedule.<br />
            <span className="gradient-text-gold">A single digital twin.</span>
          </h2>
          <p className="text-ink-muted mt-6 max-w-xl mx-auto leading-relaxed">
            Join the project teams turning embodied carbon from a reporting overhead into a competitive lever.
          </p>
          <Link
            to="/register"
            data-testid="footer-cta-trial"
            className="inline-flex items-center gap-2 mt-10 bg-gold text-black px-8 py-4 font-medium rounded-sm hover:bg-[#e0bc40] glow-gold"
          >
            Start free trial <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 text-center text-xs text-ink-dim font-mono-data tracking-widest">
        © 2026 GPI · CARBONIQ · MADE FOR THE NET-ZERO CENTURY
      </footer>
    </div>
  );
}
